import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, tap, map, Subscription, startWith, take, distinctUntilChanged, forkJoin, catchError, throttleTime, Subject, debounceTime, filter, switchMap, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent, UtilityService, PreviewMode } from 'src/app/@shared';
import { Offer, OfferDomain, OfferService, OfferTag, OfferPromoService, OfferPromo, PromoDomain, EventService, Event, ProductCategoryService, ProductCategory, MontageBuilderService, SettingsService, OfferDetailService, OfferPricingFormComponent, } from '../..';
import { CustomFieldService } from '../../services/custom-field.service';
import { CustomFieldDefinition, CustomFieldType } from '../../models/custom-field.model';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { Status } from '../../models/offer-promo-status';
import * as dayjs from 'dayjs';
import { AuthorizeControlService, CommentsComponent } from 'pr1-ui-components';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { IPrintPage } from '../../models/print-page.model';
import { WorkflowStage } from '../../models/workflow-stage.model';
import { ValidatorsService } from 'src/app/@shared/services/validators.service';
import {
  EditUndoController,
  sortOfferEditUndoChanges,
} from '../../services/edit-undo.controller';
import { isPropertyModifiedInList, getPreviousStepOldValue as lookupPreviousStepOldValue } from '../../utilities/previous-step-changes.util';
import { IACopyError, IACopyFeedbackAction, IACopyFeedbackRequest, IAGenerationOutcome, IAGenerationResponse } from '../../models/ia-generation-outcome.model';
import { IACopyEditDialogComponent, IACopyEditDialogResult } from '../ia-copy-edit-dialog/ia-copy-edit-dialog.component';
import {
  IACopyFeedbackDialogComponent,
  IACopyFeedbackDialogResult,
} from '../ia-copy-feedback-dialog/ia-copy-feedback-dialog.component';

const log = new Logger('OfferDetailsComponent');

@Component({
  selector: 'app-offer-details',
  templateUrl: './offer-details.component.html',
  styleUrls: ['./offer-details.component.scss'],
})
export class OfferDetailsComponent<TOfferDomain extends OfferDomain<Offer>, TOfferPromo extends OfferPromo, TEvent extends Event, TPromoDomain extends PromoDomain<OfferPromo>, TProductCategory extends ProductCategory,> implements OnInit, OnDestroy {
  @ViewChild('pricingForm') pricingForm?: OfferPricingFormComponent;
  offer$: Observable<OfferDomain<Offer>> = of({} as OfferDomain<Offer>);
  customFields$: Observable<CustomFieldDefinition[]> = of([]);
  customFieldValues: any = {};
  assetsforBadge: any;
  assetGroups = [];
  offertypes: any;
  countries: string[] = [];
  currencies: string[] = [];
  languages: string[] = [];
  noneValue = null;
  private versionName = '';
  private prevHeadline = '';
  showMontage: boolean = false;
  private prevOfferBodyCopy = '';
  public showJunctionAi?: boolean = false;
  tagCtrl = new FormControl<string>('', [
    Validators.minLength(1),
    Validators.maxLength(50),
  ]);
  offerTagIds: string[] = [];
  updatedOfferTagIds: string[] = [];
  searchCount: number = 0;
  pricingDetails: any = {};
  isPricingFormValid: boolean = true;
  private destroy$ = new Subject<void>();
  private exitSaveResult$ = new Subject<boolean>();
  private savingForExit = false;
  formGroup = new FormGroup({
    Detail: new FormGroup({
      ClientKey: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(200),
      ]),
      Id: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true }
      ),
      StartDate: new FormControl<Date | string | null>(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }), // Validators.pattern("[0-9 \\-/]*"
      EndDate: new FormControl<Date | string | null>(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }), // Validators.pattern("[0-9 \\-/]*"
      Name: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(100),
      ]),
      Headline: new FormControl<string>(''),
      BodyCopy: new FormControl<string>(''),
      Disclaimer: new FormControl<string>(''),
      ClientComment: new FormControl<string>(''),
      AndUp: new FormControl<string>(''),
      TargetURL: new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(5000),
      ]),
      Rank: new FormControl<number>(1, [
        Validators.min(1),
        Validators.max(9999999),
      ]),
      RewardAmount: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      RewardQuantity: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      // PageNumber: new FormControl<string>(''),
      Position: new FormControl<number | null>(null),
      PrintPageId: new FormControl<string | null>(null),
      RewardSpend: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      AdditionalDiscountAmount: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      AdditionalDiscountQuantity: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      AdditionalDiscountAmount2: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      AdditionalDiscountQuantity2: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      AdditionalDiscountAmount3: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      AdditionalDiscountQuantity3: new FormControl<number>(0, [
        Validators.min(0),
        Validators.max(9999),
      ]),
      CustomFields: new FormGroup({}),
      Country: new FormControl<string | null>(null),
      Currency: new FormControl<string | null>(null),
      Language: new FormControl<string | null>(null),
      LoyaltyIndicator: new FormControl<string | null>(null),
      OfferVariantName: new FormControl<string | null>(null),
      BadgeAssetId: new FormControl<string | null>(null),
      OfferTypeId: new FormControl<string | null>(null),
      Status: new FormControl<string>('PENDING'),
      ProductCategoryIds: new FormControl<any[]>([]),
      ParentOfferGroupId: new FormControl<string | null>(null),
      OfferGroupAssociatedRank: new FormControl<number | null>(null),
    })
  }, { updateOn: 'blur' });
  categoryCtrl = new FormControl('');
  offerServiceSubs = new Subscription();
  offerId: string = '0';
  eventId!: string;
  isRun = false;
  currentJAICreativeDetail: any;
  isSucceded = false;
  jaiCreativeAdded = false;
  maxDate: Date = new Date(9999, 0, 1);
  offerResponse: OfferDomain<Offer> = {} as OfferDomain<Offer>;
  offerpromosList: TPromoDomain[] = [];
  versionsCount: number = 0;
  readOnlyMode: any = null;
  routeQueryParams: any = {};
  isCommentDialogOpened: boolean = false;
  montageBuilderCheckConfigureStatus: boolean = false;
  PreviewMode = PreviewMode;
  category?: string = '';
  productCategories$: any[] = [] as any[];
  filteredCategory!: Observable<any[]>;
  private selectedItems: any = [];
  printPagesList: IPrintPage[] = [];
  workflowStages: WorkflowStage[] = [];
  hasSaved = false;
  dateFieldsInCustomFields: string[] = [];
  selectFieldsInCustomFields: string[] = [];
  modifiedPropertyNames: string[] = [];
  modifiedPropertyOldValues: Record<string, string> = {};
  showChangesFromPreviousStep = false;

  iaCopyRequestId = '';
  iaCopyResponses: IAGenerationResponse[] = [];
  selectedResponseIndex = 0;
  showComparePanel = false;
  showIACopyReview = false;
  isPollingIACopy = false;
  isIACopyProcessing = false;
  isIACopyReviewPending = false;
  isIACopyAwaitingSave = false;
  isIAEnabled = false;
  iaCopyInlineError = '';
  iaCopyErrorFlash = false;
  private iaCopyFeedbackByResponseId = new Map<string, 'up' | 'down'>();
  iaCopyAccepted = false;
  iaCopyFeedbackSubmitting = false;
  iaCopyCompleteSubmitting = false;
  private activeIACopyRequestId: string | null = null;
  private iaCopyPollAttempt = 0;
  private iaCopyPollStop$ = new Subject<void>();
  private iaCopyPollTimer: ReturnType<typeof setTimeout> | null = null;
  private iaCopyErrorFlashTimer: ReturnType<typeof setTimeout> | null = null;
  private iaCopyGenerationRequestId = '';
  private iaCopyGenerationResponseId = '';
  private iaCopyLockedResponseId: string | null = null;
  private iaCopyUserSelectedResponseId: string | null = null;
  private iaCopyResponseOrderLocked = false;
  private previousOfferId: string | null = null;
  private readonly maxIACopyPollAttempts = 30;

  private undoEnabled = false;
  private readonly editUndo: EditUndoController;
  private pendingPricingInit = false;
  private pendingCustomFieldsInit = false;
  private pendingUiInit = false;
  private deferCaptureUntilPricingSync = false;
  private lastKnownOfferTypeId: string | null = null;

  get canUndo(): boolean {
    return this.undoEnabled && this.editUndo.hasUndoActions && this.canEdit();
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public offerService: OfferService<Offer, TOfferDomain>,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private eventService: EventService<TEvent>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
    private authorizationService: AuthorizeControlService,
    private cdRef: ChangeDetectorRef,
    private montageBuilderService: MontageBuilderService,
    public utilityService: UtilityService,
    private categoryService: ProductCategoryService<TProductCategory>,
    private settingService: SettingsService,
    private offerDetailServ: OfferDetailService,
    private validatorsService: ValidatorsService,
    private customFieldService: CustomFieldService
  ) {
    this.editUndo = new EditUndoController({
      getSnapshot: () => this.buildUndoSnapshot(),
      applyChange: (path, value) => this.applyUndoChange(path, value),
      setCanUndo: (canUndo) => this.offerpromoService.setOfferEditCanUndo(canUndo),
      canUndo: () => this.canUndo,
      sortChangesForApply: sortOfferEditUndoChanges,
    });
  }

  ngOnInit(): void {
    this.offerpromoService.setOfferEditCanUndo(false);
    this.offerpromoService.commentDialogBoxStatus$.subscribe((status: boolean) => { this.isCommentDialogOpened = status });
    this.offerpromoService.eventId = this.route.snapshot.params['eventId'];

    this.loadCustomFields();

    this.offerServiceSubs.add(
      this.offerpromoService.offerEditUndoEnabled$.subscribe((enabled) => {
        this.undoEnabled = !!enabled;
        this.editUndo.setEnabled(this.undoEnabled);
      })
    );
    this.offerServiceSubs.add(
      this.offerpromoService.offerEditUndoRequest$.subscribe(() => {
        this.undoLastAction();
      })
    );

    this.route.params.subscribe((params) => {
      const nextOfferId = params['offerId'];
      if (this.previousOfferId && nextOfferId !== this.previousOfferId) {
        this.resetIACopyStateForNavigation(true);
      }
      this.previousOfferId = nextOfferId;
      // get the offer id from the parent route
      this.offerId = nextOfferId;
      this.eventId = params['eventId'];
      this.getPrintPageDetailsByEvent();
      this.montageBuilderService.montageBuilderStatusBehaviorSubject.next(false);
      this.montageBuilderService.montageBuilderResponseBehaviorSubject.next({});
      // if we are editing a record, get the record data
      this.offerService.getAssets().subscribe((res: any) => {
        this.assetsforBadge = res.value;
      });
      this.offerService.getOfferTypes().subscribe((res: any) => {
        this.offertypes = res.value;
      });

      forkJoin({
        countries: this.offerService.getCountries().pipe(catchError(() => of({ value: [] }))),
        currencies: this.offerService.getCurrencies().pipe(catchError(() => of({ value: [] }))),
        languages: this.offerService.getLanguages().pipe(catchError(() => of({ value: [] })))
      }).subscribe(({ countries, currencies, languages }: any) => {
        this.countries = countries.value;
        this.currencies = currencies.value;
        this.languages = languages.value;
      });

      this.categoryService.getAllCategories().subscribe({
        next: (data: any) => {
          this.productCategories$ = data.value;
          this.categoryCtrl.patchValue('');
          this.getDataById();
        },
        error: (error: any) => {
          this.getDataById();
        }
      });

      this.offerServiceSubs.add(this.offerService.saveorCancel$.pipe(throttleTime(100)).subscribe((res) => {
        if (res === '1') {
          this.save();
        } else if (res === '0') {
          this.cancel();
        }
      }));
      this.offerServiceSubs.add(
        this.offerService.variantsCount$.subscribe((res) => {
          this.versionsCount = res;
        }));

      this.readOnlyMode = this.offerpromoService.getOfferDetailsReadOnlyValue();
      this.offerpromoService.offerDetailsReadOnlyMode$.subscribe((res) => {
        this.readOnlyMode = res;
      });
    });

    this.filteredCategory = this.categoryCtrl.valueChanges.pipe(
      startWith(''),
      map((category) =>
        category ? this.filterCategory(category) : this.productCategories$.slice()
      )
    );
    this.getIntegrations();
    this.offerServiceSubs.add(this.offerpromoService.deleteRecord$.subscribe((versionName) => {
      if (versionName) {
        this.deleteRecord(versionName);
      }
    }));
    this.offerServiceSubs.add(this.formGroup.valueChanges.subscribe(() => {
      if (this.formGroup.valid && this.isPricingFormValid) {
        this.offerpromoService.setOfferDetailsValidState = true;
      } else {
        this.offerpromoService.setOfferDetailsValidState = false;
      }
    }));

    this.offerServiceSubs.add(
      this.formGroup.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.undoEnabled) return;
          if (this.editUndo.isApplyingUndo) return;
          if (this.editUndo.suppressCapture) return;
          if (this.pendingUiInit) return;
          if (this.pendingPricingInit) return;
          if (this.pendingCustomFieldsInit) return;
          if (this.deferCaptureUntilPricingSync) return;
          this.editUndo.captureIfNeeded();
        })
    );

    this.offerServiceSubs.add(this.formGroup.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => this.formGroup.valid),
        map(() => this.buildOfferPayload()),
        switchMap(offer => this.offerService.getOfferPreviewHtml(offer)),
        takeUntil(this.destroy$)
      ).subscribe((res: any) => {
        this.canEdit() && res?.PricingPreviewHtml && this.offerService.setPreviewHtml(res.PricingPreviewHtml);
      }));

    this.offerServiceSubs.add(this.offerpromoService.workflowStages$.subscribe((stages) => {
      this.workflowStages = stages;
    }));
    this.offerServiceSubs.add(this.offerpromoService.jaiCreativeDetails$.subscribe((res) => {
      this.updateDetails(res, true);
    }));

    this.offerServiceSubs.add(
      this.offerService.modifiedPropertyNames$.subscribe((names) => {
        this.modifiedPropertyNames = names ?? [];
      })
    );
    this.offerServiceSubs.add(
      this.offerService.modifiedPropertyOldValues$.subscribe((oldValues) => {
        this.modifiedPropertyOldValues = oldValues ?? {};
      })
    );
    this.offerServiceSubs.add(
      this.offerService.showChangesFromPreviousStep$.subscribe((show) => {
        this.showChangesFromPreviousStep = show;
      })
    );

    this.offerServiceSubs.add(
      this.settingService.getActiveActions().pipe(takeUntil(this.destroy$)).subscribe((res) => {
        this.isIAEnabled = res.IAEnabled;
        if (this.isIAEnabled && this.offerResponse?.IACopyRequestId?.trim()) {
          this.syncIACopyRequestFromOffer(this.offerResponse);
        }
        this.cdRef.markForCheck();
      })
    );
    this.offerServiceSubs.add(
      this.offerpromoService.iaCopyOutcome$
        .pipe(
          filter(() => this.isIAEnabled),
          filter((outcome): outcome is IAGenerationOutcome => !!outcome?.Request?.Id),
          filter(() => this.isIACopyOwnedByCurrentOffer()),
          takeUntil(this.destroy$)
        )
        .subscribe((outcome) => this.startIACopyPolling(outcome))
    );
    this.offerServiceSubs.add(
      this.offerpromoService.iaCopyProcessing$
        .pipe(takeUntil(this.destroy$))
        .subscribe((processing) => {
          this.isIACopyProcessing = processing;
          this.cdRef.markForCheck();
        })
    );
    this.offerServiceSubs.add(
      this.offerpromoService.iaCopyReviewPending$
        .pipe(takeUntil(this.destroy$))
        .subscribe((pending) => {
          this.isIACopyReviewPending = pending;
          this.cdRef.markForCheck();
        })
    );
    this.offerServiceSubs.add(
      this.offerpromoService.iaCopyAwaitingSave$
        .pipe(takeUntil(this.destroy$))
        .subscribe((awaiting) => {
          this.isIACopyAwaitingSave = awaiting;
          this.cdRef.markForCheck();
        })
    );
    this.offerServiceSubs.add(
      this.offerpromoService.iaCopyError$
        .pipe(takeUntil(this.destroy$))
        .subscribe((error) => {
          if (!this.isIAEnabled) {
            return;
          }
          this.iaCopyInlineError = error?.message ?? '';
          if (error) {
            this.triggerIACopyErrorFlash();
          }
          this.cdRef.markForCheck();
        })
    );
  }

  isPropertyModified(...propertyKeys: string[]): boolean {
    return isPropertyModifiedInList(
      this.modifiedPropertyNames,
      this.showChangesFromPreviousStep,
      ...propertyKeys
    );
  }

  getPreviousStepOldValue(...propertyKeys: string[]): string | null {
    return lookupPreviousStepOldValue(
      this.modifiedPropertyOldValues,
      this.showChangesFromPreviousStep,
      ...propertyKeys
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.formGroup.dirty && !this.hasSaved && this.canEdit()) {
      $event.preventDefault();
      $event.returnValue = true;
    }
  }

  canExit(
    route: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (this.formGroup.dirty && !this.hasSaved && !this.canEdit()) {
      this.formGroup.markAsPristine();
      this.formGroup.markAsUntouched();
      return true;
    }

    if (this.formGroup.dirty && !this.hasSaved) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Close without saving?',
          message: 'You are attempting to navigate out of the Offer details tab with unsaved changes.\nHow would you like to proceed?',
          saveText: 'Save and Leave',
          confirmText: 'Leave Without Saving',
          cancelText: 'Stay',
        },
        disableClose: true,
      });

      return dialogRef.afterClosed().pipe(
        switchMap((result) => {
          if (result === true) {
            this.formGroup.markAsPristine();
            this.formGroup.markAsUntouched();
            this.hasSaved = false;
            return of(true);
          }
          if (result === 'save') {
            this.savingForExit = true;
            this.save();
            return this.exitSaveResult$.asObservable().pipe(take(1));
          }

          return of(false);
        })
      );
    }

    return true;
  }

  private resolveExitSave(success: boolean): boolean {
    if (!this.savingForExit) return false;
    this.savingForExit = false;
    this.exitSaveResult$.next(success);
    return true;
  }

  getPrintPageDetailsByEvent() {
    this.offerpromoService.GetAllPrintPagesByEventforOffers(this.eventId).subscribe((res) => {
      this.printPagesList = res;
    })
  }

  clearData() {
    this.categoryCtrl.patchValue('');
  }

  filterCategory(name: string) {
    let arr = this.productCategories$.filter(
      (category) => category.ProductCategoryName.toLowerCase().indexOf(name.toLowerCase()) === 0
    );

    return arr.length ? arr : [];
  }

  getIntegrations() {
    this.settingService.integrations$.pipe(take(1)).subscribe((res: any) => {
      if (res) {
        const integ = res.find((i: any) => i.ClientKey === 'montagebuilder');
        if (integ) {
          this.showMontage = integ.Status;
        }
      }
    });
  }

  ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }

  updateDetails(creativeDetails: any, isRun: boolean) {
    if (!creativeDetails) {
      this.isRun = false;
      this.isSucceded = false;
      return;
    }

    this.prevHeadline = creativeDetails.Headline;
    this.prevOfferBodyCopy = creativeDetails.BodyCopy;
    this.editUndo.runSuppressed(() => {
      this.formGroup.controls.Detail.controls.Headline.patchValue(
        creativeDetails.Headline || creativeDetails.HeadLine,
        { emitEvent: false }
      );
      this.formGroup.controls.Detail.controls.BodyCopy.patchValue(creativeDetails.BodyCopy, {
        emitEvent: false,
      });
    });
    this.currentJAICreativeDetail = creativeDetails;

    if (creativeDetails.isRun !== undefined) {
      this.isRun = creativeDetails.isRun;
    } else {
      this.isRun = isRun;
    }
    if (creativeDetails.status != 'Completed') {
      this.showJunctionAi = false;
    }
  }

  // applyChanges() {
  //   this.isRun = false;
  //   this.isSucceded = true;
  //   this.jaiCreativeAdded = true;
  //   this.offerpromoService.approveJAICreative(new Date().toString())
  //   setTimeout(() => {
  //     this.isSucceded = false;
  //   }, 5000);
  // }

  revertChanges() {
    this.formGroup.controls.Detail.controls.Headline.patchValue(this.prevHeadline);
    this.formGroup.controls.Detail.controls.BodyCopy.patchValue(this.prevOfferBodyCopy);
    this.isRun = false;
  }

  navigateChildOffers() {
    this.router.navigate([`../`, `childversions`], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve'
    });
  }

  getDataById() {
    this.showJunctionAi = false;
    if (this.offerId && this.offerId !== '0') {
      this.offer$ = this.offerDetailServ.offer$.pipe(distinctUntilChanged(),
        tap((offer: any) => {
          offer = this.mergeIACopyRequestIdPatch(offer);

          this.offerTagIds = [];
          this.updatedOfferTagIds = [];

          if (offer && this.offerId === offer?.Detail?.Id) {
            if (this.formGroup.dirty && (this.isPollingIACopy || this.isIACopyProcessing)) {
              this.syncIAFieldsOnly(offer);
              return;
            }

            let responseOfferDetails = JSON.parse(JSON.stringify(offer));
            this.montageBuilderService.selectedOfferDetailsBehaviorSubject.next(offer);
            if (responseOfferDetails && responseOfferDetails.ImageEditorConfigId) {
              this.montageBuilderService.montageBuilderCheckConfigureBehaviorSubject.next(true);
              this.montageBuilderService.montageBuilderResponseBehaviorSubject.next(offer);
            } else {
              this.montageBuilderService.montageBuilderCheckConfigureBehaviorSubject.next(false);
              this.montageBuilderService.montageBuilderResponseBehaviorSubject.next(offer);
            }


            this.offerServiceSubs.add(this.montageBuilderService.montageBuilderCheckConfigure$.pipe(take(1)).subscribe((data) => {
              this.montageBuilderCheckConfigureStatus = data;
            }));

            this.offerService.currentRecord = JSON.parse(JSON.stringify(offer));
            if (offer && offer.Detail && offer.Detail.StartDate) {
              offer.Detail.StartDate = new Date(offer.Detail.StartDate);
              // console.log('Start Date', offer.Detail.StartDate);
            }
            if (offer && offer.Detail && offer.Detail.EndDate) {
              offer.Detail.EndDate = new Date(offer.Detail.EndDate);
              // console.log('End Date', offer.Detail.EndDate);
            }
            const newVersionName = this.offerService.getNewVersion;
            if (newVersionName) {
              this.offerService.getVarintClientKey(this.eventId, this.offerId, newVersionName).subscribe((res) => {
                this.formGroup.controls.Detail.controls.ClientKey.patchValue(res.value, { emitEvent: false })
              })

              offer.Detail.OfferVariantName = newVersionName;
            }
            this.customFieldValues = offer.Detail?.CustomFields || {};
            this.initUndoBaselineFromCurrentState();
            this.loadCustomFields();
            this.formGroup.patchValue(offer, { emitEvent: false });
            this.setNulls();
            this.formGroup.controls.Detail.controls.BadgeAssetId.patchValue(offer?.BadgeAssetId as string, { emitEvent: false });
            this.formGroup.controls.Detail.controls.OfferTypeId.patchValue(offer?.OfferTypeId as string, { emitEvent: false });
            this.formGroup.controls.Detail.controls.PrintPageId.patchValue(offer?.PrintPageId as string, { emitEvent: false });
            // this.formGroup.controls.Detail.controls.PageNumber.patchValue(offer?.PageNumber as string);
            this.formGroup.controls.Detail.controls.Position.patchValue(offer?.Position as number, { emitEvent: false });
            if (offer.Position == -1) {
              this.formGroup.controls.Detail.controls.Position.patchValue(null, { emitEvent: false });
            }

            this.formGroup.markAllAsTouched();
            this.offerResponse = offer;
            this.montageBuilderService.creativeId = offer.JAICreativeMontageId
            this.syncIACopyRequestFromOffer(offer);

            if (offer.OfferTagIds && offer.OfferTagIds.length) {
              this.offerTagIds = offer.OfferTagIds.filter((id: string): id is string => id !== null);
              this.updatedOfferTagIds = offer.OfferTagIds.filter((id: string): id is string => id !== null);
            } else {
              this.offerTagIds = [];
              this.updatedOfferTagIds = [];
            }

            if (offer.ProductCategoryIds && offer.ProductCategoryIds.length > 0) {
              const categories = this.productCategories$.filter((x: any) =>
                offer.ProductCategoryIds?.includes(x.Id)
              );
              this.applyProductCategorySelection(categories);
            } else {
              this.applyProductCategorySelection([]);
            }

            if (offer.ParentOfferGroupId == undefined) {
              offer.ParentOfferGroupId = null;
            }

            if (offer.OfferGroupAssociatedRank == undefined) {
              offer.OfferGroupAssociatedRank = -1;
            }
            this.formGroup.controls.Detail.controls.ParentOfferGroupId.patchValue(offer?.ParentOfferGroupId, { emitEvent: false });
            this.formGroup.controls.Detail.controls.OfferGroupAssociatedRank.patchValue(offer?.OfferGroupAssociatedRank, { emitEvent: false });

            if (
              this.showIACopyReview
              && this.iaCopyResponses.length
              && this.activeIACopyRequestId === (offer?.IACopyRequestId?.trim() ?? '')
            ) {
              this.applySelectedIACopy();
            }

            if (offer.ParentOfferGroupId) {
              this.formGroup.controls.Detail.controls.Rank.disable();
            }
          }
        })
      );
    } else {
      this.eventService.getEventDomain(this.eventId).subscribe((res) => {
        this.formGroup.controls.Detail.controls.StartDate.patchValue(
          new Date(res?.Detail.StartDate as any)
        );
        this.formGroup.controls.Detail.controls.EndDate.patchValue(
          new Date(res?.Detail.EndDate as any)
        );
      });
      this.offerService.getBaseClientKey(this.eventId).subscribe((res) => {
        this.formGroup.controls.Detail.controls.ClientKey.patchValue(res.value);
      })
    }
  }

  updateSelectedItems(matSelect: MatSelect, category: any) {
    const current = [...(this.selectedItems || [])];
    const checkedItems = matSelect.options.filter((x) => x.selected).map((i) => i.value);
    if (checkedItems?.length && checkedItems.find((x) => x.Id === category.Id)) {
      this.selectedItems = current.some((c) => c.Id === category.Id)
        ? current
        : [...current, category];
    } else {
      this.selectedItems = current.filter((i: any) => i.Id !== category.Id);
    }
  }

  validateDate(): boolean {
    let startDate = this.formGroup.controls.Detail.controls.StartDate;
    let endDate = this.formGroup.controls.Detail.controls.EndDate;
    if (endDate && endDate.value && startDate && startDate.value) {
      if (endDate.value < startDate.value) {
        this.matSnackBar.open(
          'End Date must be equal to or greater than Start Date',
          'Error',
          { verticalPosition: 'top', panelClass: ['snackbar-error'] }
        );
        return false;
      }
    }
    return true;
  }

  onPricingFormValueChange($event: any) {
    this.pricingDetails = $event;

    if (this.canEdit()) {
      let offerPayload = this.buildOfferPayload();
      this.offerService.getOfferPreviewHtml(offerPayload).subscribe((res: any) => {
        this.offerService.setPreviewHtml(res.PricingPreviewHtml);
      });
    }

    if (this.pendingPricingInit) {
      this.pendingPricingInit = false;
      this.tryFinalizeUndoBaseline();
      return;
    }

    if (this.deferCaptureUntilPricingSync) {
      this.deferCaptureUntilPricingSync = false;
      if (this.canCaptureUndoForUserActions()) {
        this.editUndo.captureIfNeeded();
        this.syncLastKnownOfferTypeId();
      }
      return;
    }

    if (this.undoEnabled && !this.editUndo.isApplyingUndo && !this.editUndo.suppressCapture) {
      this.editUndo.captureIfNeeded();
    }
  }

  onOfferTypeSelectionChange(event: MatSelectChange) {
    if (this.editUndo.isApplyingUndo || this.editUndo.suppressCapture) return;
    if (this.pendingPricingInit || this.pendingCustomFieldsInit || this.pendingUiInit) {
      return;
    }
    const nextId = (event.value as string | null) ?? null;
    if (nextId === this.lastKnownOfferTypeId) return;
    this.deferCaptureUntilPricingSync = true;
  }

  onOfferTypePanelClosed() {
    if (!this.deferCaptureUntilPricingSync) return;
    this.deferCaptureUntilPricingSync = false;
    if (this.canCaptureUndoForUserActions()) {
      this.editUndo.captureIfNeeded();
      this.syncLastKnownOfferTypeId();
    }
  }

  onPricingFormValidityChange($event: boolean) {
    this.isPricingFormValid = $event;

    if (this.formGroup.valid && this.isPricingFormValid) {
      this.offerpromoService.setOfferDetailsValidState = true;
    } else {
      this.offerpromoService.setOfferDetailsValidState = false;
    }
  }

  getSelectedOfferTypeId(): any {
    const offerTypeId = this.formGroup.controls.Detail.controls.OfferTypeId.value;
    return offerTypeId;
  }

  save() {
    if (!this.canEdit()) {
      this.resolveExitSave(true);
      return;
    }
    this.clearUndoStack();
    this.saveOffer();
    this.offerService.setSaveorCancel('');
  }

  clearNulls(offer: OfferDomain<Offer>): any {
    if (offer.Detail.RewardAmount == null) offer.Detail.RewardAmount = 0;
    if (offer.Detail.RewardQuantity == null) offer.Detail.RewardQuantity = 0;
    if (offer.Detail.RewardSpend == null) offer.Detail.RewardSpend = 0;
    if (offer.Detail.AdditionalDiscountAmount == null)
      offer.Detail.AdditionalDiscountAmount = -1;
    if (offer.Detail.AdditionalDiscountQuantity == null)
      offer.Detail.AdditionalDiscountQuantity = -1;
    if (offer.Detail.AdditionalDiscountAmount2 == null)
      offer.Detail.AdditionalDiscountAmount2 = -1;
    if (offer.Detail.AdditionalDiscountQuantity2 == null)
      offer.Detail.AdditionalDiscountQuantity2 = -1;
    if (offer.Detail.AdditionalDiscountAmount3 == null)
      offer.Detail.AdditionalDiscountAmount3 = -1;
    if (offer.Detail.AdditionalDiscountQuantity3 == null)
      offer.Detail.AdditionalDiscountQuantity3 = -1;
    if (offer.Detail.Position == null)
      offer.Detail.Position = -1;

    return offer;
  }

  setNulls(): any {
    if (this.formGroup.controls.Detail.controls.RewardAmount.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.RewardAmount.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.RewardQuantity.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.RewardQuantity.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.RewardSpend.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.RewardSpend.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.AdditionalDiscountAmount.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.AdditionalDiscountAmount.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.AdditionalDiscountQuantity.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.AdditionalDiscountQuantity.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.AdditionalDiscountAmount2.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.AdditionalDiscountAmount2.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.AdditionalDiscountQuantity2.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.AdditionalDiscountQuantity2.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.AdditionalDiscountAmount3.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.AdditionalDiscountAmount3.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.AdditionalDiscountQuantity3.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.AdditionalDiscountQuantity3.setValue(null, { emitEvent: false });
    if (this.formGroup.controls.Detail.controls.Position.getRawValue() == -1)
      this.formGroup.controls.Detail.controls.Position.setValue(null, { emitEvent: false });
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    });
  }

  formatDate(date: Date | string | null) {
    if (date) {
      const newDate = dayjs(date).format('MM/DD/YYYY');
      return newDate;
    } else {
      return '';
    }
  }

  buildOfferPayload(): OfferDomain<Offer> {
    let offer = this.formGroup.getRawValue() as unknown as OfferDomain<Offer>;

    // Dates
    offer.Detail.StartDate = this.formatDate(
      this.formGroup.controls.Detail.controls.StartDate.value
    );
    offer.Detail.EndDate = this.formatDate(
      this.formGroup.controls.Detail.controls.EndDate.value
    );

    // Merge pricing + clean nulls
    offer = this.clearNulls(offer);
    offer.Detail = {
      ...offer.Detail,
      ...this.pricingDetails,
      Country: this.formGroup.controls.Detail.controls.Country.value || null,
      Currency: this.formGroup.controls.Detail.controls.Currency.value || null,
      Language: this.formGroup.controls.Detail.controls.Language.value || null
    };

    // Tags
    offer.OfferTagIds = this.updatedOfferTagIds;
    // Product Categories
    const categories = this.formGroup.controls.Detail.controls.ProductCategoryIds.value;
    offer.ProductCategoryIds = categories
      ? categories.map((cat: any) => cat.Id)
      : [];

    // Versions
    offer.VersionIds = this.offerService.selectedVersionsIds;

    // Badge
    offer.BadgeAssetId = this.formGroup.controls.Detail.controls.BadgeAssetId.value as string;
    delete offer.Detail.BadgeAssetId;

    // Offer Type
    offer.OfferTypeId = this.formGroup.controls.Detail.controls.OfferTypeId.value as string;
    delete offer.Detail.OfferTypeId;
    offer.OfferTypeName =
      this.offertypes?.find((x: any) => x.Id === offer.OfferTypeId)?.OfferTypeName || '';

    // Position / Page
    offer.Position = this.formGroup.controls.Detail.controls.Position.value as number;
    offer.PrintPageId = this.formGroup.controls.Detail.controls.PrintPageId.value as string;
    delete offer.Detail.Position;
    delete offer.Detail.PrintPageId;

    // Offer Group
    offer.ParentOfferGroupId =
      this.formGroup.controls.Detail.controls.ParentOfferGroupId.value as string;
    offer.OfferGroupAssociatedRank =
      this.formGroup.controls.Detail.controls.OfferGroupAssociatedRank.value as number;

    return offer;
  }

  saveOffer() {
    (document.activeElement as HTMLElement)?.blur();
    this.pricingForm?.commitPendingEdits();

    this.trimControlValues(this.formGroup);

    this.formGroup.markAsDirty();
    this.formGroup.markAllAsTouched();
    if (!(this.formGroup.valid && this.isPricingFormValid)) {
      if (this.savingForExit) {
        this.matSnackBar.open('Please fix validation errors before saving.', 'Error', {
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
        this.resolveExitSave(false);
      }
      return;
    }

    if (!this.validateDate()) {
      this.resolveExitSave(false);
      return;
    }

    if (this.isIACopyAwaitingSave && !this.shouldIgnoreIACopyForOffer()) {
      this.completeIACopyThenSave();
      return;
    }

    this.executeSaveOffer();
  }

  private completeIACopyThenSave(): void {
    if (this.iaCopyCompleteSubmitting) {
      return;
    }

    const requestId = this.iaCopyGenerationRequestId?.trim()
      || this.iaCopyRequestId?.trim()
      || this.offerResponse?.IACopyRequestId?.trim()
      || '';
    const responseId = this.iaCopyGenerationResponseId?.trim()
      || this.getSelectedIACopyResponse()?.Id?.trim()
      || '';

    if (!requestId || !responseId) {
      this.publishIACopyError({
        type: 'complete_generation_failed',
        message: 'Unable to complete IA copy generation. Request or response id is missing.',
      });
      this.resolveExitSave(false);
      this.cdRef.markForCheck();
      return;
    }

    this.iaCopyCompleteSubmitting = true;
    this.offerpromoService.setIACopyError(null);
    this.iaCopyInlineError = '';

    this.settingService.completeGeneration(requestId, responseId).subscribe({
      next: () => {
        this.iaCopyCompleteSubmitting = false;
        this.executeSaveOffer();
        this.cdRef.markForCheck();
      },
      error: (err) => {
        this.iaCopyCompleteSubmitting = false;
        this.publishIACopyError({
          type: 'complete_generation_failed',
          message: this.parseIACopyErrorMessage(err, 'Failed to complete IA copy generation. Please try again.'),
        });
        this.resolveExitSave(false);
        this.cdRef.markForCheck();
      },
    });
  }

  private parseIACopyErrorMessage(err: any, fallback: string): string {
    try {
      const body = err?.error;
      if (Array.isArray(body)) {
        return body.join(', ');
      }
      if (body?.value?.length) {
        return body.value.join(', ');
      }
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
    } catch (e) {
      log.error('Error parsing IA copy error message:', e);
    }
    return fallback;
  }

  private executeSaveOffer() {
    let offer = this.buildOfferPayload();
    const customFields = Object.keys(this.formGroup.controls.Detail.controls.CustomFields.controls);
    if (customFields && customFields.length > 0) {
        customFields.forEach((fieldName) => {
          if (this.dateFieldsInCustomFields.includes(fieldName) && this.formGroup.controls.Detail.controls.CustomFields.get(fieldName)?.value) {
            offer.Detail.CustomFields[fieldName] = this.formatDate(this.formGroup.controls.Detail.controls.CustomFields.get(fieldName)?.value);
          }
        });
      }
      const objecttoPass =
        this.offerId !== '0' ? this.getUpdatedRecordInfo(offer) : offer;
      const offers =
        this.offerService.selection.selected &&
          this.offerService.selection.selected.length > 0
          ? this.offerService.selection.selected.map((x) => x.Id)
          : [this.offerId];

      let apiToCall;
      if (
        objecttoPass.Detail &&
        (!objecttoPass.Detail.Id || objecttoPass.Detail.Id === '0')
      ) {
        this.getUpdatedPrintDetails(objecttoPass);
        apiToCall = this.offerService.saveOffer(objecttoPass, this.eventId);
      } else {
        delete objecttoPass.Properties?.Position;
        delete objecttoPass.Properties?.PrintPageId;
        if (objecttoPass.Properties &&
          Object.keys(objecttoPass.Properties).length === 0 &&
          objecttoPass.Properties.constructor === Object) delete objecttoPass.Properties;
        this.getUpdatedPrintDetails(objecttoPass);
        if (!this.offerService.getNewVersion && this.jaiCreativeAdded && this.currentJAICreativeDetail) {
          if (this.prevHeadline != this.formGroup.controls.Detail.controls.Headline.value || this.prevOfferBodyCopy != this.formGroup.controls.Detail.controls.BodyCopy.value) {
            objecttoPass.Properties["JAIDetails"] = `${this.currentJAICreativeDetail.Id}||${this.formGroup.controls.Detail.controls.Headline.value}||${this.formGroup.controls.Detail.controls.BodyCopy.value}`;
          } else {
            objecttoPass.Properties["JAIDetails"] = `${this.currentJAICreativeDetail.Id}`;
          }
        }

        apiToCall = this.offerService.getNewVersion
          ? this.offerService.cloneOffer(offer.Detail.Id, offer)
          : this.offerService.saveOfferProperties(offers, objecttoPass);
      }

      apiToCall.subscribe({
        next: (response: any) => {
          this.hasSaved = true;
          this.formGroup.markAsPristine();
          this.resetIACopyUiAfterSave();
          this.offerService.newVersion = '';
          this.offerpromoService.jaiCreativeInfo(null);
          if (this.savingForExit) {
            if (response?.Id) {
              this.offerId = response.Id;
            }
            this.offerService.reload();
            this.offerDetailServ.reload();
            this.resolveExitSave(true);
            return;
          }
          if (response) {
            const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
              data: {
                title: 'Saved Successfully',
                //message: 'Saved Successfully',
                confirmText: 'Continue Editing',
                cancelText: 'Back to Offers',
              },
              disableClose: true,
            });
            confirmDialog.afterClosed().subscribe((confirmResult) => {
              if (confirmResult) {
                this.routeQueryParams = this.route.snapshot.queryParams;
                this.offerId = response.Id;
                this.offerService.reload();
                this.offerDetailServ.reload();

                const currentUrlTree = this.router.createUrlTree(
                  [`../../${response.Id}/details`],
                  {
                    relativeTo: this.route,
                    queryParams: this.routeQueryParams,
                    queryParamsHandling: 'merge',
                  }
                );

                this.router.navigateByUrl(currentUrlTree, { skipLocationChange: false }).then(() => {
                  this.formGroup.markAsPristine();
                  this.formGroup.markAsUntouched();
                  this.offerService.reload();
                  this.getPrintPageDetailsByEvent();
                });
              } else {
                this.offerpromoService.reload();
                this.router.navigate([`../../`], {
                  relativeTo: this.route,
                  queryParamsHandling: 'preserve'
                });
              }
            });
          }
        },
        error: (error) => {
          this.resolveExitSave(false);
          if (error.status === 500) {
            log.error('500 Error saving offer', error);
            this.matSnackBar.open('500 Error saving offer', 'Error', {
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;

            let messages: string[] = [];

            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                let propertyPath = validation.PropertyName || '';

                propertyPath = propertyPath.replace('OfferDomain.', '').replace('Detail.', '');

                const message = typeof validation === 'string' ? validation : validation.ErrorMessage || 'Validation error occurred';
                messages.push(message);

                const control = this.formGroup?.get(`Detail.${propertyPath}`) || this.formGroup?.controls?.Detail?.get(propertyPath);

                if (control) {
                  control.markAsTouched();
                  control.setErrors({ invalid: message });
                }
              });
            }

            else if (apiValidations?.errors) {
              Object.keys(apiValidations.errors).forEach((key) => {
                const fieldErrors = apiValidations.errors[key];
                fieldErrors.forEach((msg: string) => messages.push(msg));

                const control = this.formGroup?.get(`Detail.${key}`);
                if (control) {
                  control.setErrors({ invalid: fieldErrors[0] });
                }
              });
            }

            else if (apiValidations?.Message) {
              messages.push(apiValidations.Message);
            }

            else if (typeof apiValidations === 'string') {
              messages.push(apiValidations);
            }

            else {
              messages.push('Something went wrong. Please try again.');
            }

            this.matSnackBar.open(messages.join('\n'), 'Error', {
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
        },
      });
  }
  openMontageEditor() {
    // Need to load the Motage Editor
    this.montageBuilderService.montageBuilderStatusBehaviorSubject.next(true);

    this.router.navigate([`../`, `montage-editor`], { relativeTo: this.route, queryParamsHandling: 'merge' });

  }

  getUpdatedPrintDetails(objecttoPass: any) {
    if (objecttoPass.PrintPageId || objecttoPass.PrintPageId == "" || objecttoPass.Position || objecttoPass.Position == null) {
      objecttoPass['PrintPageId'] = this.formGroup.controls.Detail.controls.PrintPageId
        .value as string;
      this.formGroup.controls.Detail.controls.Position
        .value as number == null ? objecttoPass['Position'] = -1 : objecttoPass['Position'] = this.formGroup.controls.Detail.controls.Position
          .value as number;
    }
  }

  cancel(): void {
    this.offerService.setSaveorCancel('');
    if (this.formGroup.dirty && this.canEdit() == true) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Close without saving?',
          message: 'This offer has unsaved changes',
          confirmText: 'Continue Editing',
          cancelText: 'Close',
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe((confirmResult) => {
        if (!confirmResult) {
          this.clearUndoStack();
          this.formGroup.reset();
          this.router.navigate([`../../`], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve'
          });
        }
      });
    } else {
      this.clearUndoStack();
      this.formGroup.reset();
      this.router.navigate([`../../`], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve'
      });
    }
  }


  getUpdatedObject(selectedRecords: any) {
    let datatoReturn = [];
    if (selectedRecords && selectedRecords.length > 0) {
      for (let index = 0; index <= selectedRecords.length - 1; index++) {
        if (!selectedRecords[index].Detail) {
          datatoReturn.push({
            Detail: selectedRecords[index],
            EventOfferType: 'OFFER',
          });
        } else {
          datatoReturn.push(selectedRecords[index]);
        }
      }
    }
    return datatoReturn;
  }

  deleteRecord(versionName?: string) {
    if (versionName == 'Base' || this.canEdit() == false) return;
    const record = this.formGroup.getRawValue();
    const variantName = record.Detail?.OfferVariantName;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: variantName
          ? `Are you sure you want to delete "${record.Detail.Name} (${variantName})"?`
          : `Are you sure you want to delete ${record.Detail.Name}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((confirmResult) => {
      if (confirmResult) {
        this.offerService
          .deleteOffer(record.Detail.Id, this.eventId)
          .subscribe({
            next: () => {
              this.matSnackBar.open(
                `${record.Detail.Name} deleted`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.offerpromoService.reload();
              this.router.navigate([`../../`], {
                relativeTo: this.route,
                queryParamsHandling: 'preserve'
              });
            },
            error: (error) => {
              log.error('Error deleting offer', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            },
          });
      }
    });
  }

  deleteBaseRecord() {
    if (this.canEdit() == false) {
      return;
    }
    const record = this.formGroup.getRawValue();
    const variantName = record.Detail?.OfferVariantName;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: variantName
          ? `Are you sure you want to delete "${record.Detail.Name} (${variantName})"?`
          : `Are you sure you want to delete ${record.Detail.Name}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((confirmResult) => {
      if (confirmResult) {
        this.offerService
          .deleteOfferVersions(record.Detail.Id, this.eventId)
          .subscribe({
            next: () => {
              this.matSnackBar.open(
                `${record.Detail.Name} deleted`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.offerpromoService.reload();
              this.router.navigate([`../../`], {
                relativeTo: this.route,
                queryParamsHandling: 'preserve'
              });
            },
            error: (error) => {
              log.error('Error deleting offer', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            },
          });
      }
    });
  }

  tagSelected(tagId: string): void {
    if (this.updatedOfferTagIds.includes(tagId)) {
      return;
    }
    this.applyUserOfferTagIds([...this.updatedOfferTagIds, tagId]);
  }

  addTag(offerTag: OfferTag): void {
    if (this.updatedOfferTagIds.includes(offerTag.Id)) {
      return;
    }
    this.applyUserOfferTagIds([...this.updatedOfferTagIds, offerTag.Id]);
    this.matSnackBar.open(`${offerTag.OfferTagName} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG);
  }

  removeTag(tagId: string): void {
    this.applyUserOfferTagIds(this.updatedOfferTagIds.filter(id => id !== tagId));
  }

  canEdit(): boolean {
    const hasAccess =
      this.authorizationService.checkAccess('edit||offermang') &&
      this.readOnlyMode != true;
    if (hasAccess) {
      this.formGroup.disabled ? this.formGroup.enable() : null;
      this.tagCtrl.enable();
    } else {
      this.formGroup.enabled ? this.formGroup.disable() : null;
      this.tagCtrl.disable();
    }

    return hasAccess;
  }

  getUpdatedRecordInfo(record: any) {
    const allowedNullProps = new Set([
      'Detail.Country',
      'Detail.Currency',
      'Detail.Language'
    ]);
    const objecttoPass: any = {
      Properties: {},
    };
    const currentOfferInfo = this.offerService.getCurrentRecord;
    if (currentOfferInfo) {
      const properties = Object.keys(record);
      for (let index = 0; index <= properties.length - 1; index++) {
        if (
          record[properties[index]] != null &&
          record[properties[index]] != undefined &&
          typeof record[properties[index]] === 'object' &&
          !Array.isArray(record[properties[index]])
        ) {
          const childProperties = Object.keys(record[properties[index]]);
          if (childProperties && childProperties.length > 0) {
            const currentRecord = currentOfferInfo[properties[index]];
            const updatedRecord = record[properties[index]];
            for (let childIndex = 0; childIndex <= childProperties.length - 1; childIndex++) {
              const value = updatedRecord[childProperties[childIndex]];
              const propPath = `${properties[index]}.${childProperties[childIndex]}`;
              const isAllowedNull = allowedNullProps.has(propPath);

              if (currentRecord[childProperties[childIndex]] !== value && (value !== undefined && (value !== null || isAllowedNull))) {
                objecttoPass['Properties'][childProperties[childIndex]] = value;
              }
            }
          }
        } else if (
          (!Array.isArray(currentOfferInfo[properties[index]]) &&
            currentOfferInfo[properties[index]] !==
            record[properties[index]]) ||
          (Array.isArray(currentOfferInfo[properties[index]]) &&
            !this.areArraysEqual(
              currentOfferInfo[properties[index]],
              record[properties[index]]
            ))
        ) {
          objecttoPass[properties[index]] = record[properties[index]];
        }
      }
    }
    return objecttoPass;
  }
  areArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1?.length !== arr2?.length) {
      return false;
    }
    const sortedArr1 = arr1?.slice().sort();
    const sortedArr2 = arr2?.slice().sort();
    for (let i = 0; i < sortedArr1?.length; i++) {
      if (sortedArr1[i] !== sortedArr2[i]) {
        return false;
      }
    }
    return true;
  }
  openDialog($event: any, offer: any) {
    if (this.isCommentDialogOpened == false) {
      let commentElementStyle = document.getElementById(offer.DomainId) as HTMLElement;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.hasBackdrop = true;
      dialogConfig.disableClose = false;
      dialogConfig.data = {
        headline: offer.Detail.Headline,
        channel: 'OFFER',
        parentId: offer.Detail.Id,
        clientKey: offer.Detail.ClientKey,
        bodyCopy: offer.Detail.BodyCopy,
        workflowStage: this.workflowStages.length ? this.workflowStages[0] : null,
      };
      let confirmDialog = this.dialog.open(CommentsComponent, dialogConfig);
      confirmDialog.afterOpened().subscribe(() => {
        if (commentElementStyle) {
          commentElementStyle.style.color = "blue";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(true);
        }
      });
      confirmDialog.afterClosed().subscribe((data) => {
        if (data > 0) {
          Object(offer).CommentsCount = data;
        }
        if (commentElementStyle) {
          commentElementStyle.style.color = "black";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(false);
        }
      });
    }
  }

  clearSearch() {
    this.categoryCtrl.patchValue('');
    this.commitProductCategorySelection();
  }

  onCategoryRemoved(category: any) {
    this.editUndo.applyUserMutation(
      () => {
        const next = (this.selectedItems || []).filter((c: any) => c.Id !== category?.Id);
        this.applyProductCategorySelection(next);
      },
      () => this.canCaptureUndoForUserActions()
    );
  }

  private commitProductCategorySelection(): void {
    const selection = [...(this.selectedItems || [])];
    this.editUndo.applyUserMutation(
      () => this.applyProductCategorySelection(selection),
      () => this.canCaptureUndoForUserActions()
    );
  }

  private applyProductCategorySelection(categories: any[]): void {
    const next = [...(categories || [])];
    this.selectedItems = next;
    this.formGroup.controls.Detail.controls.ProductCategoryIds.setValue(next, {
      emitEvent: false,
    });
  }

  private getSelectedCategoryIds(): string[] {
    const categories =
      this.selectedItems?.length > 0
        ? this.selectedItems
        : ((this.formGroup.controls.Detail.controls.ProductCategoryIds.value as any[]) || []);
    return categories
      .map((category: any) => category?.Id)
      .filter((id: string | undefined): id is string => !!id)
      .sort();
  }

  private resolveCategoriesByIds(ids: string[]): any[] {
    if (!ids?.length) {
      return [];
    }
    const idSet = new Set(ids);
    return this.productCategories$.filter((category: any) => idSet.has(category.Id));
  }

  private loadCustomFields(): void {
    this.customFields$ = this.customFieldService.getCustomFieldDefinitionsByVertexType('OFFER').pipe(
      tap((fields: CustomFieldDefinition[]) => {
        const finalizeAfterCustomFieldsLoaded = this.pendingCustomFieldsInit;
        this.editUndo.runSuppressed(() => {
          const customFieldsGroup = this.formGroup.controls.Detail.controls.CustomFields as FormGroup;

          fields.forEach((field: CustomFieldDefinition) => {
            if (!customFieldsGroup.get(field.FieldName)) {
              const formControl = new FormControl<any>({ value: null, disabled: field.IsReadOnly });
              if (field.FieldType === CustomFieldType.Toggle || field.FieldType === CustomFieldType.Checkbox) {
                formControl.setValue(false, { emitEvent: false });
              }
              customFieldsGroup.addControl(field.FieldName, formControl);
              if (field.FieldType === CustomFieldType.Date) {
                this.dateFieldsInCustomFields.push(field.FieldName);
              }
              if (field.FieldType === CustomFieldType.MultiSelect) {
                this.selectFieldsInCustomFields.push(field.FieldName);
              }
            }
          });
          if (Object.keys(this.customFieldValues).length > 0) {
            Object.keys(this.customFieldValues).forEach(key => {
              if (customFieldsGroup.get(key)) {
                if (this.dateFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                  customFieldsGroup.get(key)?.setValue(new Date(this.customFieldValues[key]), {
                    emitEvent: false,
                  });
                } else if (this.selectFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                  customFieldsGroup.get(key)?.setValue(
                    typeof this.customFieldValues[key] === 'string'
                      ? [this.customFieldValues[key]]
                      : this.customFieldValues[key],
                    { emitEvent: false }
                  );
                } else {
                  customFieldsGroup.get(key)?.setValue(this.customFieldValues[key], { emitEvent: false });
                }
              }
            });
          }
        });

        if (finalizeAfterCustomFieldsLoaded) {
          this.pendingCustomFieldsInit = false;
          this.tryFinalizeUndoBaseline();
        }
      })
    );
  }

  getCustomFieldsGroup(): FormGroup {
    return this.formGroup.controls.Detail.controls.CustomFields as FormGroup;
  }

  clearUndoStack() {
    this.editUndo.clearStack();
  }

  undoLastAction() {
    if (!this.editUndo.undoLast()) {
      return;
    }
    this.deferCaptureUntilPricingSync = false;
    this.syncLastKnownOfferTypeId();
  }

  onDateCommitted() {
    this.captureUndoOnFieldCommit();
  }

  onRankCommitted() {
    this.captureUndoOnFieldCommit();
  }

  private captureUndoOnFieldCommit(): void {
    this.editUndo.captureOnFieldCommit(() => this.canCaptureUndoForUserActions());
  }

  private applyUndoChange(path: string, value: any) {
    if (path.startsWith('pricing.')) {
      const key = path.slice('pricing.'.length);
      // Pricing form uses `null` internally but emits `-1` for nulls.
      const patch: Record<string, any> = { [key]: value === -1 ? null : value };
      this.pricingForm?.applyExternalValuePatch(patch);
      // pricingDetails stores the emitted (normalized) pricing payload (where nulls become -1)
      this.pricingDetails = { ...(this.pricingDetails || {}), [key]: value };
      return;
    }

    if (path === 'updatedOfferTagIds') {
      this.applyOfferTagIds(Array.isArray(value) ? value : []);
      return;
    }

    if (path === 'productCategoryIds' || path === 'Detail.ProductCategoryIds') {
      const categories =
        path === 'productCategoryIds'
          ? this.resolveCategoriesByIds(Array.isArray(value) ? value : [])
          : Array.isArray(value)
            ? [...value]
            : [];
      this.applyProductCategorySelection(categories);
      return;
    }

    const ctrl = this.formGroup.get(path);
    if (ctrl) {
      ctrl.setValue(value, { emitEvent: false });
      ctrl.updateValueAndValidity({ emitEvent: false });
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      if (path === 'Detail.OfferTypeId') {
        this.cdRef.detectChanges();
      }
    }
  }

  private buildUndoSnapshot(): any {
    const detail = this.formGroup.getRawValue()?.Detail ?? {};
    const { ProductCategoryIds: _categories, ...detailWithoutCategories } = detail;
    return {
      Detail: detailWithoutCategories,
      productCategoryIds: this.getSelectedCategoryIds(),
      pricing: { ...(this.pricingDetails || {}) },
      updatedOfferTagIds: [...(this.updatedOfferTagIds || [])],
    };
  }

  private initUndoBaselineFromCurrentState() {
    this.clearUndoStack();
    if (!this.undoEnabled) {
      this.pendingPricingInit = false;
      this.pendingCustomFieldsInit = false;
      this.editUndo.suppressCapture = false;
      return;
    }
    this.pendingPricingInit = true;
    this.pendingCustomFieldsInit = true;
    this.editUndo.suppressCapture = true;
    this.pendingUiInit = true;
    setTimeout(() => {
      this.pendingUiInit = false;
    });
  }

  /** After offer load, establish undo baseline only when pricing and custom fields have finished initializing. */
  private tryFinalizeUndoBaseline(): void {
    if (!this.undoEnabled) return;
    if (this.pendingPricingInit || this.pendingCustomFieldsInit) return;

    this.editUndo.finalizeBaselineFromLoad();
    this.syncLastKnownOfferTypeId();
  }

  private syncLastKnownOfferTypeId(): void {
    this.lastKnownOfferTypeId =
      this.formGroup.controls.Detail.controls.OfferTypeId.value ?? null;
  }

  private applyOfferTagIds(tagIds: string[]): void {
    const next = [...tagIds];
    this.updatedOfferTagIds = next;
    this.offerTagIds = [...next];
  }

  private canCaptureUndoForUserActions(): boolean {
    return (
      this.undoEnabled &&
      !this.editUndo.isApplyingUndo &&
      !this.editUndo.suppressCapture &&
      !this.pendingUiInit &&
      !this.pendingPricingInit &&
      !this.pendingCustomFieldsInit
    );
  }

  private areTagIdListsEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, index) => id === b[index]);
  }

  private applyUserOfferTagIds(nextIds: string[]): void {
    const normalized = [...nextIds];
    if (this.areTagIdListsEqual(this.updatedOfferTagIds, normalized)) {
      return;
    }

    this.editUndo.applyUserMutation(
      () => this.applyOfferTagIds(normalized),
      () => this.canCaptureUndoForUserActions()
    );
  }

  ngOnDestroy() {
    this.resetIACopyStateForNavigation();
    this.destroy$.next();
    this.destroy$.complete();
    this.clearUndoStack();
    this.dialog.closeAll();
    this.offerpromoService.deleteRecord("");
    this.offerServiceSubs.unsubscribe();
  }

  onRankInput(event: any) {
    const filtered = this.validatorsService.allowOnlyDigits(event);
    const numericValue = filtered ? Number(filtered) : null;
    this.formGroup.controls.Detail.controls['Rank']?.setValue(numericValue, { emitEvent: false });
  }

  private mergeIACopyRequestIdPatch(offer: any): any {
    if (!offer || offer?.Detail?.Id !== this.offerId || offer.IACopyRequestId?.trim()) {
      return offer;
    }
    const patchedRequestId = this.offerDetailServ.getIACopyRequestIdPatch(this.offerId);
    if (!patchedRequestId) {
      return offer;
    }
    return { ...offer, IACopyRequestId: patchedRequestId };
  }

  private syncIAFieldsOnly(offer: any): void {
    if (this.shouldIgnoreIACopyForOffer(offer)) {
      return;
    }
    const requestId = offer?.IACopyRequestId?.trim() ?? '';
    this.iaCopyRequestId = requestId;
    if (this.offerResponse) {
      this.offerResponse = { ...this.offerResponse, IACopyRequestId: offer.IACopyRequestId };
    }
    this.syncIACopyRequestFromOffer(offer);
  }

  private clearIARequestIdLocally(): void {
    this.iaCopyRequestId = '';
    if (this.offerResponse) {
      this.offerResponse = { ...this.offerResponse, IACopyRequestId: undefined };
    }
  }

  private publishIACopyError(error: IACopyError): void {
    this.offerpromoService.setIACopyError(error);
  }

  private triggerIACopyErrorFlash(): void {
    this.clearIACopyErrorFlashTimer();
    this.iaCopyErrorFlash = true;
    this.iaCopyErrorFlashTimer = setTimeout(() => {
      this.iaCopyErrorFlash = false;
      this.iaCopyErrorFlashTimer = null;
      this.iaCopyInlineError = '';
      this.offerpromoService.setIACopyError(null);
      this.cdRef.markForCheck();
    }, 5000);
  }

  private clearIACopyErrorFlashTimer(): void {
    if (this.iaCopyErrorFlashTimer) {
      clearTimeout(this.iaCopyErrorFlashTimer);
      this.iaCopyErrorFlashTimer = null;
    }
    this.iaCopyErrorFlash = false;
  }

  private markIACopyRequestSettled(requestId?: string | null): void {
    this.offerpromoService.markIACopyRequestSettled(requestId);
  }

  private isIACopyOwnedByCurrentOffer(): boolean {
    return this.offerpromoService.isIACopyForOffer(this.offerId);
  }

  private shouldIgnoreIACopyForOffer(offer?: any): boolean {
    const requestId = offer?.IACopyRequestId?.trim()
      ?? this.offerResponse?.IACopyRequestId?.trim()
      ?? this.iaCopyRequestId?.trim()
      ?? '';
    return !this.isIAEnabled && !!requestId;
  }

  private shouldResumeIACopyPolling(requestId: string): boolean {
    if (!requestId) {
      return false;
    }
    if (this.isPollingIACopy && this.activeIACopyRequestId === requestId) {
      return false;
    }
    return true;
  }

  private syncIACopyRequestFromOffer(offer: any): void {
    if (this.shouldIgnoreIACopyForOffer(offer)) {
      this.iaCopyRequestId = '';
      this.offerpromoService.setIACopyError(null);
      this.iaCopyInlineError = '';
      this.clearIACopyErrorFlashTimer();
      return;
    }
    const requestId = offer?.IACopyRequestId?.trim()
      ?? this.offerDetailServ.getIACopyRequestIdPatch(this.offerId)
      ?? '';
    this.iaCopyRequestId = requestId;
    if (!this.shouldResumeIACopyPolling(requestId)) {
      return;
    }
    const offerIdAtSync = this.offerId;
    setTimeout(() => {
      if (this.offerId !== offerIdAtSync) {
        return;
      }
      if (!this.shouldResumeIACopyPolling(requestId)) {
        return;
      }
      if (!this.offerpromoService.getIACopySourceOfferId()) {
        this.offerpromoService.setIACopySourceOfferId(this.offerId);
      }
      this.startIACopyPolling({ Request: { Id: requestId } });
    });
  }

  startIACopyPolling(outcome: IAGenerationOutcome): void {
    if (!this.isIAEnabled) {
      this.offerpromoService.setIACopyProcessing(false);
      return;
    }
    const sourceId = this.offerpromoService.getIACopySourceOfferId();
    if (sourceId && sourceId !== this.offerId) {
      return;
    }
    const requestId = outcome?.Request?.Id?.trim();
    if (!requestId || this.offerpromoService.isIACopyRequestSettled(requestId)) {
      this.offerpromoService.setIACopyProcessing(false);
      return;
    }
    if (this.activeIACopyRequestId === requestId && this.isPollingIACopy) {
      return;
    }

    this.stopIACopyPolling();
    this.offerpromoService.setIACopySourceOfferId(this.offerId);
    this.activeIACopyRequestId = requestId;
    this.iaCopyRequestId = requestId;
    this.iaCopyGenerationRequestId = requestId;
    this.isPollingIACopy = true;
    this.iaCopyPollAttempt = 0;
    this.offerpromoService.setIACopyProcessing(true);
    this.offerpromoService.setIACopyError(null);
    this.offerpromoService.setIACopyReviewPending(false);
    this.offerpromoService.setIACopyAwaitingSave(false);
    this.applyIACopyPollResponses(outcome);
    this.continueIACopyPolling(requestId, outcome);
  }

  private handleIACopyPollFailure(message: string): void {
    if (!this.isIACopyOwnedByCurrentOffer()) {
      return;
    }
    this.markIACopyRequestSettled(this.activeIACopyRequestId);
    this.isPollingIACopy = false;
    this.activeIACopyRequestId = null;
    this.clearIARequestIdLocally();
    this.resetIACopyReviewPanel();
    this.offerpromoService.setIACopyProcessing(false);
    this.offerpromoService.setIACopyReviewPending(false);
    this.offerpromoService.setIACopyAwaitingSave(false);
    this.publishIACopyError({ type: 'poll_failed', message });
    this.cdRef.markForCheck();
  }

  private continueIACopyPolling(requestId: string, outcome: IAGenerationOutcome): void {
    if (!this.isIACopyOwnedByCurrentOffer()) {
      return;
    }
    this.applyIACopyPollResponses(outcome);
    if (this.isPollingIACopy && this.areAllResponsesTerminal(outcome, this.iaCopyPollAttempt > 0)) {
      this.onIACopyPollingComplete(outcome);
      return;
    }
    if (this.iaCopyPollAttempt >= this.maxIACopyPollAttempts) {
      this.handleIACopyPollFailure('Copy generation timed out. Please try again.');
      return;
    }

    const needsRefresh = this.iaCopyPollAttempt > 0 || !(outcome.Responses?.length);
    const source$ = needsRefresh
      ? this.settingService.refreshGeneration(requestId)
      : of(outcome);

    source$.pipe(takeUntil(this.iaCopyPollStop$), takeUntil(this.destroy$)).subscribe({
      next: (current) => {
        if (!this.isIACopyOwnedByCurrentOffer()) {
          return;
        }
        this.iaCopyPollAttempt++;
        if (!this.isPollingIACopy) {
          return;
        }
        this.applyIACopyPollResponses(current);
        if (this.isPollingIACopy && this.areAllResponsesTerminal(current, true)) {
          this.onIACopyPollingComplete(current);
        } else if (this.isPollingIACopy) {
          this.clearIACopyPollTimer();
          this.iaCopyPollTimer = setTimeout(() => {
            this.iaCopyPollTimer = null;
            this.continueIACopyPolling(requestId, current);
          }, 5000);
        }
      },
      error: () => {
        if (!this.isIACopyOwnedByCurrentOffer()) {
          return;
        }
        this.handleIACopyPollFailure('Copy generation failed. Please try again.');
      },
    });
  }

  private areAllResponsesTerminal(outcome: IAGenerationOutcome, treatEmptyAsTerminal = false): boolean {
    const responses = outcome.Responses ?? [];
    if (!responses.length) {
      return treatEmptyAsTerminal;
    }
    return responses.every((r) => r.Status === 'Completed' || r.Status === 'Failed');
  }

  private onIACopyPollingComplete(outcome: IAGenerationOutcome): void {
    if (!this.isIACopyOwnedByCurrentOffer()) {
      return;
    }
    this.iaCopyGenerationRequestId =
      outcome?.Request?.Id?.trim()
      || this.activeIACopyRequestId?.trim()
      || this.iaCopyRequestId?.trim()
      || '';
    this.markIACopyRequestSettled(outcome?.Request?.Id);
    this.isPollingIACopy = false;
    this.activeIACopyRequestId = null;
    this.clearIARequestIdLocally();

    const responses = (outcome.Responses ?? []).filter(
      (r) => r.Status === 'Completed' || r.Status === 'Failed'
    );

    if (!responses.length) {
      this.offerpromoService.setIACopyProcessing(false);
      this.offerpromoService.setIACopyReviewPending(false);
      this.offerpromoService.setIACopyAwaitingSave(false);
      this.resetIACopyReviewPanel();
      const notes = outcome?.Request?.Notes?.trim();
      this.publishIACopyError({
        type: 'empty',
        message: notes || 'No requests were generated.',
      });
      this.cdRef.markForCheck();
      return;
    }

    const allFailed = responses.every((r) => r.Status === 'Failed');
    if (allFailed) {
      this.offerpromoService.setIACopyProcessing(false);
      this.offerpromoService.setIACopyReviewPending(false);
      this.offerpromoService.setIACopyAwaitingSave(false);
      this.resetIACopyReviewPanel();
      const notes = outcome?.Request?.Notes?.trim();
      this.publishIACopyError({
        type: 'all_failed',
        message: notes || 'Copy generation failed. Please try again.',
      });
      this.cdRef.markForCheck();
      return;
    }

    this.mergeIACopyPollResponsesById(responses);
    this.iaCopyResponses = this.iaCopyResponses.filter(
      (r) => !this.isIACopyPlaceholderResponse(r)
        && (r.Status === 'Completed' || r.Status === 'Failed')
    );
    this.showIACopyReview = true;
    this.showComparePanel = this.iaCopyResponses.length > 1;
    this.hydrateIACopyFeedbackFromResponses(this.iaCopyResponses);
    this.offerpromoService.setIACopyProcessing(false);
    this.offerpromoService.setIACopyReviewPending(true);
    this.offerpromoService.setIACopyError(null);
    this.iaCopyInlineError = '';
    if (!this.isIACopySelectionLocked()) {
      this.syncIACopySelectionAfterPollUpdate();
    } else {
      const lockedIndex = this.findResponseIndexById(this.iaCopyLockedResponseId);
      if (lockedIndex >= 0) {
        this.selectedResponseIndex = lockedIndex;
      }
    }
    this.cdRef.markForCheck();
  }

  private isIACopySelectionLocked(): boolean {
    return this.iaCopyAccepted || !!this.iaCopyLockedResponseId?.trim();
  }

  private isIACopyResponseTerminal(status?: string): boolean {
    const normalizedStatus = status?.trim();
    return normalizedStatus === 'Completed' || normalizedStatus === 'Failed';
  }

  private findResponseIndexById(id?: string | null): number {
    const trimmedId = id?.trim();
    if (!trimmedId) {
      return -1;
    }
    return this.iaCopyResponses.findIndex((response) => response.Id?.trim() === trimmedId);
  }

  private findFirstCompletedIndex(): number {
    return this.iaCopyResponses.findIndex(
      (response) => response.Status === 'Completed'
    );
  }

  private findAcceptedOrEditedIndex(): number {
    return this.iaCopyResponses.findIndex(
      (response) => response.Status === 'Completed'
        && (response.FeedbackAction === 'Accepted' || response.FeedbackAction === 'Edited')
    );
  }

  private syncIACopySelectionAfterPollUpdate(): void {
    if (this.isIACopySelectionLocked()) {
      return;
    }

    const acceptedOrEditedIndex = this.findAcceptedOrEditedIndex();
    if (acceptedOrEditedIndex >= 0) {
      this.selectedResponseIndex = acceptedOrEditedIndex;
      this.applySelectedIACopy();
      return;
    }

    const userSelectedId = this.iaCopyUserSelectedResponseId?.trim();
    if (userSelectedId) {
      const userIndex = this.findResponseIndexById(userSelectedId);
      const userResponse = userIndex >= 0 ? this.iaCopyResponses[userIndex] : null;
      if (userResponse
        && !this.isIACopyResponseFailed(userResponse)
        && !this.isIACopyResponsePending(userResponse)) {
        this.selectedResponseIndex = userIndex;
        this.applySelectedIACopy();
        return;
      }
    }

    const firstCompletedIndex = this.findFirstCompletedIndex();
    if (firstCompletedIndex >= 0) {
      this.selectedResponseIndex = firstCompletedIndex;
      this.applySelectedIACopy();
    }
  }

  get isIACopyPanelActive(): boolean {
    return (this.showIACopyReview && this.iaCopyResponses.length > 0) || this.iaCopyAccepted;
  }

  get showIACopyReviewActions(): boolean {
    if (!this.showIACopyReview || !this.iaCopyResponses.length || this.iaCopyAccepted) {
      return false;
    }
    return this.iaCopyResponses.some((response) => response.Status === 'Completed');
  }

  isIACopyResponseFailed(response: IAGenerationResponse): boolean {
    return response.Status === 'Failed';
  }

  isIACopyResponsePending(response: IAGenerationResponse): boolean {
    if (this.isIACopyPlaceholderResponse(response)) {
      return true;
    }
    const status = response.Status?.trim();
    return status !== 'Completed' && status !== 'Failed';
  }

  private isIACopyPlaceholderResponse(response: IAGenerationResponse): boolean {
    return !!response.Id?.startsWith('__pending_');
  }

  private applyIACopyPollResponses(outcome: IAGenerationOutcome): void {
    if (!this.isPollingIACopy) {
      return;
    }

    const responses = outcome.Responses ?? [];
    if (responses.length) {
      this.mergeIACopyPollResponsesById(responses);
    }

    if (this.iaCopyResponses.length) {
      this.showIACopyReview = true;
      this.showComparePanel = true;
    }
    this.syncIACopySelectionAfterPollUpdate();
    this.cdRef.markForCheck();
  }

  private mergeIACopyResponseById(
    existing: IAGenerationResponse,
    incoming: IAGenerationResponse
  ): IAGenerationResponse {
    if (this.isPollingIACopy && this.isIACopyResponseTerminal(existing.Status)) {
      return existing;
    }

    const merged = { ...existing, ...incoming };
    const id = existing.Id?.trim();
    const isLocked = id && id === this.iaCopyLockedResponseId?.trim();
    const hasLocalFeedback = existing.FeedbackAction === 'Accepted'
      || existing.FeedbackAction === 'Edited'
      || existing.FeedbackAction === 'Rejected';

    if (isLocked || hasLocalFeedback) {
      merged.FeedbackAction = existing.FeedbackAction;
      merged.CorrectedHeadline = existing.CorrectedHeadline;
      merged.CorrectedBodyCopy = existing.CorrectedBodyCopy;
      merged.FeedbackNotes = existing.FeedbackNotes;
      merged.FeedbackCategory = existing.FeedbackCategory;
    }

    return merged;
  }

  private mergeIACopyPollResponsesById(incoming: IAGenerationResponse[]): void {
    const responses = incoming.filter((r) => r.Id?.trim());
    if (!responses.length) {
      return;
    }

    const existing = this.iaCopyResponses;
    const onlyPlaceholders = existing.length > 0
      && existing.every((r) => this.isIACopyPlaceholderResponse(r));

    if (!existing.length || onlyPlaceholders) {
      this.iaCopyResponses = responses.map((r) => ({ ...r }));
      this.iaCopyResponseOrderLocked = true;
      return;
    }

    const indexById = new Map<string, number>();
    existing.forEach((r, index) => {
      const id = r.Id?.trim();
      if (id && !this.isIACopyPlaceholderResponse(r)) {
        indexById.set(id, index);
      }
    });
    const merged = existing.map((r) => ({ ...r }));

    for (const incomingResponse of responses) {
      const id = incomingResponse.Id!.trim();
      const index = indexById.get(id);
      if (index !== undefined) {
        merged[index] = this.mergeIACopyResponseById(merged[index], incomingResponse);
      } else {
        indexById.set(id, merged.length);
        merged.push({ ...incomingResponse });
      }
    }

    this.iaCopyResponses = merged;
  }

  getIACopyDisplayHeadline(response: IAGenerationResponse): string {
    return (response.CorrectedHeadline?.trim() || response.Headline) ?? '';
  }

  getIACopyDisplayBodyCopy(response: IAGenerationResponse): string {
    return (response.CorrectedBodyCopy?.trim() || response.BodyCopy) ?? '';
  }

  private clearIACopyPollTimer(): void {
    if (this.iaCopyPollTimer) {
      clearTimeout(this.iaCopyPollTimer);
      this.iaCopyPollTimer = null;
    }
  }

  private resetIACopyReviewPanel(): void {
    this.showIACopyReview = false;
    this.iaCopyResponses = [];
    this.selectedResponseIndex = 0;
    this.showComparePanel = false;
    this.clearIACopyFeedbackMap();
    this.clearIACopySelectionState();
  }

  private clearIACopySelectionState(): void {
    this.iaCopyLockedResponseId = null;
    this.iaCopyUserSelectedResponseId = null;
    this.iaCopyResponseOrderLocked = false;
  }

  private stopIACopyPolling(): void {
    this.clearIACopyPollTimer();
    this.iaCopyPollStop$.next();
    this.iaCopyPollStop$ = new Subject<void>();
  }

  private stopIACopyPollingAfterUserFeedback(): void {
    if (!this.isPollingIACopy) {
      return;
    }
    this.stopIACopyPolling();
    this.isPollingIACopy = false;
    this.activeIACopyRequestId = null;
    this.offerpromoService.setIACopyProcessing(false);
    if (!this.iaCopyAccepted) {
      this.offerpromoService.setIACopyReviewPending(true);
    }
  }

  private resetIACopyStateForNavigation(clearSettledRequestIds = false): void {
    const resumeRequestId = !clearSettledRequestIds && !this.iaCopyAccepted && !this.isIACopyAwaitingSave
      ? (this.activeIACopyRequestId?.trim()
        || this.iaCopyRequestId?.trim()
        || this.iaCopyGenerationRequestId?.trim()
        || this.offerDetailServ.getIACopyRequestIdPatch(this.offerId)
        || '')
      : '';

    this.stopIACopyPolling();
    this.clearIACopyErrorFlashTimer();
    this.showIACopyReview = false;
    this.iaCopyResponses = [];
    this.selectedResponseIndex = 0;
    this.showComparePanel = false;
    this.clearIACopyFeedbackMap();
    this.iaCopyAccepted = false;
    this.iaCopyFeedbackSubmitting = false;
    this.iaCopyCompleteSubmitting = false;
    this.isIACopyAwaitingSave = false;
    this.isPollingIACopy = false;
    this.activeIACopyRequestId = null;
    this.iaCopyPollAttempt = 0;
    this.iaCopyInlineError = '';
    this.clearIACopyGenerationIds();
    this.clearIACopySelectionState();
    if (clearSettledRequestIds) {
      this.offerpromoService.clearSettledIACopyRequestIds();
      this.offerDetailServ.clearIACopyRequestIdPatch();
      this.offerpromoService.resetIACopyState();
    } else {
      if (resumeRequestId) {
        this.offerpromoService.unmarkIACopyRequestSettled(resumeRequestId);
      }
      this.offerpromoService.setIACopyProcessing(false);
      this.offerpromoService.setIACopyReviewPending(false);
      this.offerpromoService.setIACopyAwaitingSave(false);
      this.offerpromoService.setIACopyError(null);
      this.offerpromoService.setIACopyOutcome(null);
    }
    this.cdRef.markForCheck();
  }

  optionLabel(index: number): string {
    return String.fromCharCode(65 + index);
  }

  trackIACopyResponse(index: number, response: IAGenerationResponse): string {
    return response.Id?.trim() || `placeholder-${index}`;
  }

  selectIACopyOption(index: number): void {
    if (this.isPollingIACopy || this.iaCopyFeedbackSubmitting) {
      return;
    }
    const response = this.iaCopyResponses[index];
    if (!response || this.isIACopyResponseFailed(response) || this.isIACopyResponsePending(response)) {
      return;
    }
    this.iaCopyUserSelectedResponseId = response.Id?.trim() ?? null;
    this.selectedResponseIndex = index;
    this.applySelectedIACopy();
  }

  applySelectedIACopy(): void {
    if (this.isIACopySelectionLocked()) {
      return;
    }

    const selected = this.iaCopyResponses[this.selectedResponseIndex];
    if (!selected) {
      return;
    }

    if (this.isIACopyResponseFailed(selected) || this.isIACopyResponsePending(selected)) {
      return;
    }

    this.prevHeadline = this.getIACopyDisplayHeadline(selected);
    this.prevOfferBodyCopy = this.getIACopyDisplayBodyCopy(selected);
    this.editUndo.runSuppressed(() => {
      this.formGroup.controls.Detail.controls.Headline.patchValue(this.getIACopyDisplayHeadline(selected), { emitEvent: false });
      this.formGroup.controls.Detail.controls.BodyCopy.patchValue(this.getIACopyDisplayBodyCopy(selected), { emitEvent: false });
    });
    this.syncOfferDetailsValidState();
  }

  private syncOfferDetailsValidState(): void {
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.offerpromoService.setOfferDetailsValidState = this.formGroup.valid && this.isPricingFormValid;
  }

  toggleComparePanel(): void {
    this.showComparePanel = !this.showComparePanel;
  }

  private getSelectedIACopyResponse(): IAGenerationResponse | null {
    return this.iaCopyResponses[this.selectedResponseIndex] ?? null;
  }

  get iaCopyFeedback(): 'up' | 'down' | null {
    const responseId = this.getSelectedIACopyResponse()?.Id?.trim();
    if (!responseId) {
      return null;
    }
    return this.iaCopyFeedbackByResponseId.get(responseId) ?? null;
  }

  private getIACopyFeedbackForResponse(responseId: string): 'up' | 'down' | null {
    return this.iaCopyFeedbackByResponseId.get(responseId) ?? null;
  }

  private setIACopyFeedbackForResponse(responseId: string, feedback: 'up' | 'down'): void {
    this.iaCopyFeedbackByResponseId.set(responseId, feedback);
  }

  private clearIACopyFeedbackMap(): void {
    this.iaCopyFeedbackByResponseId.clear();
  }

  private hydrateIACopyFeedbackFromResponses(responses: IAGenerationResponse[]): void {
    const preservedFeedback = new Map(this.iaCopyFeedbackByResponseId);
    this.clearIACopyFeedbackMap();
    for (const response of responses) {
      const id = response.Id?.trim();
      if (!id) {
        continue;
      }
      if (response.FeedbackAction === 'Rejected') {
        this.setIACopyFeedbackForResponse(id, 'down');
      } else if (preservedFeedback.has(id)) {
        this.setIACopyFeedbackForResponse(id, preservedFeedback.get(id)!);
      }
    }
    preservedFeedback.forEach((feedback, id) => {
      if (!this.iaCopyFeedbackByResponseId.has(id)) {
        this.setIACopyFeedbackForResponse(id, feedback);
      }
    });
  }

  private buildIACopyFeedbackPayload(
    action: IACopyFeedbackAction,
    corrections?: { headline: string; bodyCopy: string },
    notes?: string,
    category?: string,
    response?: IAGenerationResponse | null
  ): IACopyFeedbackRequest {
    const selected = response ?? this.getSelectedIACopyResponse();
    const payload: IACopyFeedbackRequest = {
      Action: action,
      OriginalHeadline: selected?.Headline ?? '',
      OriginalBodyCopy: selected?.BodyCopy ?? '',
      Disclaimer: this.formGroup.controls.Detail.controls.Disclaimer.value ?? '',
    };

    if (action === 'Edited' && corrections) {
      payload.CorrectedHeadline = corrections.headline;
      payload.CorrectedBodyCopy = corrections.bodyCopy;
    }

    const trimmedNotes = notes?.trim();
    if (trimmedNotes) {
      if (action === 'Rejected') {
        payload.RejectionNotes = trimmedNotes;
      } else if (action === 'Edited') {
        payload.CorrectionNotes = trimmedNotes;
      }
    }

    const trimmedCategory = category?.trim();
    if (trimmedCategory) {
      payload.Category = trimmedCategory;
    }

    return payload;
  }

  private openIACopyFeedbackDialog(
    initialCategory?: string,
    initialNotes?: string,
    options?: { submitLabel?: string; showSkip?: boolean }
  ): Observable<IACopyFeedbackDialogResult | undefined> {
    const dialogRef = this.dialog.open(IACopyFeedbackDialogComponent, {
      minWidth: '420px',
      maxWidth: '480px',
      data: {
        initialCategory,
        initialNotes,
        submitLabel: options?.submitLabel,
        showSkip: options?.showSkip,
      },
    });

    return dialogRef.afterClosed();
  }

  private applyIACopyFeedbackToResponse(
    responseId: string,
    action: IACopyFeedbackAction,
    notes?: string,
    corrections?: { headline: string; bodyCopy: string },
    category?: string
  ): void {
    const index = this.iaCopyResponses.findIndex((r) => r.Id?.trim() === responseId);
    if (index < 0) {
      return;
    }

    const updated: IAGenerationResponse = {
      ...this.iaCopyResponses[index],
      FeedbackAction: action,
    };

    const trimmedNotes = notes?.trim();
    if (trimmedNotes) {
      updated.FeedbackNotes = trimmedNotes;
    }

    const trimmedCategory = category?.trim();
    if (trimmedCategory) {
      updated.FeedbackCategory = trimmedCategory;
    }

    if (action === 'Edited' && corrections) {
      updated.CorrectedHeadline = corrections.headline;
      updated.CorrectedBodyCopy = corrections.bodyCopy;
    }

    this.iaCopyResponses = [
      ...this.iaCopyResponses.slice(0, index),
      updated,
      ...this.iaCopyResponses.slice(index + 1),
    ];
  }

  private clearIACopyGenerationIds(): void {
    this.iaCopyGenerationRequestId = '';
    this.iaCopyGenerationResponseId = '';
  }

  private resetIACopyUiAfterSave(): void {
    this.showIACopyReview = false;
    this.iaCopyResponses = [];
    this.selectedResponseIndex = 0;
    this.showComparePanel = false;
    this.iaCopyAccepted = false;
    this.clearIACopyFeedbackMap();
    this.clearIACopySelectionState();
    this.clearIACopyGenerationIds();
    this.iaCopyFeedbackSubmitting = false;
    this.iaCopyCompleteSubmitting = false;
    this.isIACopyAwaitingSave = false;
    this.iaCopyInlineError = '';
    this.clearIACopyErrorFlashTimer();
    this.offerDetailServ.clearIACopyRequestIdPatch();
    this.offerpromoService.resetIACopyState();
    this.cdRef.markForCheck();
  }

  private completeIACopyAcceptance(responseId: string): void {
    this.iaCopyLockedResponseId = responseId;
    this.iaCopyGenerationResponseId = responseId;
    this.setIACopyFeedbackForResponse(responseId, 'up');
    this.iaCopyAccepted = true;
    this.showComparePanel = false;
    this.offerpromoService.setIACopyReviewPending(false);
    this.offerpromoService.setIACopyAwaitingSave(true);
    this.offerpromoService.setIACopyError(null);
    this.iaCopyInlineError = '';
    this.syncOfferDetailsValidState();
  }

  private submitIACopyFeedbackPayload(body: IACopyFeedbackRequest, onSuccess: () => void): void {
    const selected = this.getSelectedIACopyResponse();
    const responseId = selected?.Id?.trim();
    if (!responseId) {
      this.publishIACopyError({
        type: 'feedback_failed',
        message: 'Unable to submit feedback. Response id is missing.',
      });
      this.cdRef.markForCheck();
      return;
    }
    if (this.iaCopyFeedbackSubmitting) {
      return;
    }

    this.iaCopyFeedbackSubmitting = true;
    this.offerpromoService.setIACopyError(null);
    this.iaCopyInlineError = '';

    this.settingService.submitFeedback(responseId, body).subscribe({
      next: () => {
        this.iaCopyFeedbackSubmitting = false;
        if (body.Action === 'Accepted' || body.Action === 'Edited') {
          this.stopIACopyPollingAfterUserFeedback();
        }
        onSuccess();
        this.cdRef.markForCheck();
      },
      error: () => {
        this.iaCopyFeedbackSubmitting = false;
        this.publishIACopyError({
          type: 'feedback_failed',
          message: 'Failed to submit feedback. Please try again.',
        });
        this.cdRef.markForCheck();
      },
    });
  }

  onIACopyThumbsUp(): void {
    if (!this.isIAEnabled || this.iaCopyAccepted || this.iaCopyFeedbackSubmitting) {
      return;
    }

    const selected = this.getSelectedIACopyResponse();
    const responseId = selected?.Id?.trim();
    if (!responseId || this.isIACopyResponseFailed(selected!)) {
      return;
    }
    if (this.getIACopyFeedbackForResponse(responseId) === 'up') {
      return;
    }

    this.submitIACopyFeedbackPayload(this.buildIACopyFeedbackPayload('Accepted'), () => {
      this.completeIACopyAcceptance(responseId);
    });
  }

  onIACopyThumbsDown(): void {
    if (!this.isIAEnabled || this.iaCopyAccepted || this.iaCopyFeedbackSubmitting) {
      return;
    }

    const selected = this.getSelectedIACopyResponse();
    const responseId = selected?.Id?.trim();
    if (!responseId) {
      return;
    }
    if (this.getIACopyFeedbackForResponse(responseId) === 'down') {
      return;
    }

    this.openIACopyFeedbackDialog(selected?.FeedbackCategory, selected?.FeedbackNotes).pipe(
      filter((result): result is IACopyFeedbackDialogResult => !!result),
      takeUntil(this.destroy$)
    ).subscribe((feedback) => {
      this.submitIACopyFeedbackPayload(
        this.buildIACopyFeedbackPayload('Rejected', undefined, feedback.notes, feedback.category),
        () => {
          this.applyIACopyFeedbackToResponse(responseId, 'Rejected', feedback.notes, undefined, feedback.category);
          this.setIACopyFeedbackForResponse(responseId, 'down');
          this.offerpromoService.setIACopyError(null);
          this.iaCopyInlineError = '';
        }
      );
    });
  }

  onIACopyEdit(): void {
    if (!this.isIAEnabled || this.iaCopyAccepted || this.iaCopyFeedbackSubmitting || this.isPollingIACopy) {
      return;
    }

    const selected = this.getSelectedIACopyResponse();
    const responseId = selected?.Id?.trim();
    if (!selected || !responseId || this.isIACopyResponseFailed(selected)) {
      return;
    }

    this.showComparePanel = false;

    const dialogRef = this.dialog.open(IACopyEditDialogComponent, {
      minWidth: '760px',
      data: {
        originalHeadline: selected.Headline ?? '',
        originalBodyCopy: selected.BodyCopy ?? '',
        editableHeadline: this.getIACopyDisplayHeadline(selected),
        editableBodyCopy: this.getIACopyDisplayBodyCopy(selected),
        initialCategory: selected.FeedbackCategory ?? '',
        initialNotes: selected.FeedbackNotes ?? '',
      },
    });

    dialogRef.afterClosed().subscribe((result: IACopyEditDialogResult | undefined) => {
      if (!result) {
        return;
      }

      if (!result.hasChanges && this.getIACopyFeedbackForResponse(responseId) === 'up') {
        return;
      }

      if (result.hasChanges) {
        const corrections = { headline: result.headline, bodyCopy: result.bodyCopy };
        this.submitIACopyFeedbackPayload(
          this.buildIACopyFeedbackPayload('Edited', corrections, result.notes, result.category),
          () => {
            this.applyIACopyFeedbackToResponse(responseId, 'Edited', result.notes, corrections, result.category);
            this.formGroup.controls.Detail.controls.Headline.patchValue(result.headline, { emitEvent: false });
            this.formGroup.controls.Detail.controls.BodyCopy.patchValue(result.bodyCopy, { emitEvent: false });
            this.syncOfferDetailsValidState();
            this.completeIACopyAcceptance(responseId);
          }
        );
        return;
      }

      this.submitIACopyFeedbackPayload(this.buildIACopyFeedbackPayload('Accepted'), () => {
        this.completeIACopyAcceptance(responseId);
      });
    });
  }

  onIACopyRegenerate(response?: IAGenerationResponse): void {
    if (!this.isIAEnabled || this.iaCopyAccepted || this.iaCopyFeedbackSubmitting || this.isPollingIACopy) {
      return;
    }

    const target = response ?? this.getSelectedIACopyResponse();
    const responseId = target?.Id?.trim();
    if (!target || !responseId || this.isIACopyResponsePending(target)) {
      return;
    }

    const requestId = this.getIACopyRequestIdForRegenerate();
    if (!requestId) {
      this.publishIACopyError({
        type: 'regenerate_failed',
        message: 'Unable to regenerate copy. Request id is missing.',
      });
      this.cdRef.markForCheck();
      return;
    }

    this.openIACopyFeedbackDialog(undefined, undefined, {
      submitLabel: 'Regenerate',
      showSkip: false,
    }).pipe(
      filter((result): result is IACopyFeedbackDialogResult => !!result),
      takeUntil(this.destroy$)
    ).subscribe((feedback) => {
      const body = this.buildIACopyFeedbackPayload(
        'Rejected',
        undefined,
        feedback.notes,
        feedback.category,
        target
      );
      this.submitIACopyRegenerate(
        this.settingService.regenerateCopyResponse(requestId, responseId, body),
        requestId,
        responseId
      );
    });
  }

  onIACopyRegenerateAll(): void {
    if (!this.isIAEnabled || this.iaCopyAccepted || this.iaCopyFeedbackSubmitting || this.isPollingIACopy) {
      return;
    }
    if (this.iaCopyResponses.length <= 1) {
      return;
    }

    const requestId = this.getIACopyRequestIdForRegenerate();
    if (!requestId) {
      this.publishIACopyError({
        type: 'regenerate_failed',
        message: 'Unable to regenerate copy. Request id is missing.',
      });
      this.cdRef.markForCheck();
      return;
    }

    this.openIACopyFeedbackDialog(undefined, undefined, {
      submitLabel: 'Regenerate all',
      showSkip: false,
    }).pipe(
      filter((result): result is IACopyFeedbackDialogResult => !!result),
      takeUntil(this.destroy$)
    ).subscribe((feedback) => {
      const body = this.buildIACopyFeedbackPayload(
        'Rejected',
        undefined,
        feedback.notes,
        feedback.category
      );
      this.submitIACopyRegenerate(
        this.settingService.regenerateAllCopyResponses(requestId, body),
        requestId
      );
    });
  }

  private getIACopyRequestIdForRegenerate(): string {
    return this.iaCopyGenerationRequestId?.trim()
      || this.iaCopyRequestId?.trim()
      || this.activeIACopyRequestId?.trim()
      || this.offerResponse?.IACopyRequestId?.trim()
      || this.offerDetailServ.getIACopyRequestIdPatch(this.offerId)
      || '';
  }

  private submitIACopyRegenerate(
    api$: Observable<IAGenerationOutcome>,
    requestId: string,
    responseId?: string
  ): void {
    if (this.iaCopyFeedbackSubmitting) {
      return;
    }

    const responsesBeforeRegenerate = this.iaCopyResponses.map((r) => ({ ...r }));
    this.iaCopyFeedbackSubmitting = true;
    this.offerpromoService.setIACopyProcessing(true);
    this.offerpromoService.setIACopyError(null);
    this.iaCopyInlineError = '';
    this.markIACopyResponsesPendingForRegenerate(responseId);

    api$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (outcome) => {
        this.iaCopyFeedbackSubmitting = false;
        const outcomeRequestId = outcome?.Request?.Id?.trim() || requestId;
        const outcomeWithRequest: IAGenerationOutcome = {
          ...outcome,
          Request: {
            ...(outcome?.Request ?? {}),
            Id: outcomeRequestId,
          },
        };
        this.offerpromoService.unmarkIACopyRequestSettled(outcomeRequestId);
        this.offerDetailServ.setIACopyRequestIdPatch(this.offerId, outcomeRequestId);
        this.offerpromoService.setIACopySourceOfferId(this.offerId);
        this.applyRegeneratedIACopyOutcome(outcomeWithRequest, responseId);
        this.startIACopyPolling(outcomeWithRequest);
        this.cdRef.markForCheck();
      },
      error: () => {
        this.iaCopyFeedbackSubmitting = false;
        this.offerpromoService.setIACopyProcessing(false);
        this.iaCopyResponses = responsesBeforeRegenerate;
        this.showComparePanel = this.iaCopyResponses.length > 1;
        this.publishIACopyError({
          type: 'regenerate_failed',
          message: 'Failed to regenerate copy. Please try again.',
        });
        this.cdRef.markForCheck();
      },
    });
  }

  private markIACopyResponsesPendingForRegenerate(responseId?: string): void {
    this.showComparePanel = true;
    if (responseId?.trim()) {
      const id = responseId.trim();
      this.iaCopyResponses = this.iaCopyResponses.map((r) =>
        r.Id?.trim() === id ? { ...r, Status: 'Pending' } : r
      );
    } else {
      this.iaCopyResponses = this.iaCopyResponses.map((r) => ({ ...r, Status: 'Pending' }));
    }
    this.cdRef.markForCheck();
  }

  private applyRegeneratedIACopyOutcome(outcome: IAGenerationOutcome, responseId?: string): void {
    const incoming = (outcome.Responses ?? []).filter((r) => r.Id?.trim());
    if (!incoming.length) {
      return;
    }

    if (!responseId) {
      this.iaCopyResponses = incoming.map((r) => ({ ...r }));
      this.clearIACopyFeedbackMap();
      this.iaCopyLockedResponseId = null;
      this.iaCopyUserSelectedResponseId = null;
      this.iaCopyAccepted = false;
      this.showComparePanel = this.iaCopyResponses.length > 1
        || this.iaCopyResponses.some((r) => this.isIACopyResponsePending(r));
      return;
    }

    const regenerated = incoming.find((r) => r.Id?.trim() === responseId) ?? incoming[0];
    const id = regenerated.Id?.trim() ?? responseId;
    const index = this.findResponseIndexById(id);
    if (index >= 0) {
      const next = [...this.iaCopyResponses];
      next[index] = { ...regenerated };
      this.iaCopyResponses = next;
    } else {
      this.iaCopyResponses = [...this.iaCopyResponses, { ...regenerated }];
    }

    this.iaCopyFeedbackByResponseId.delete(id);
    if (this.iaCopyLockedResponseId?.trim() === id) {
      this.iaCopyLockedResponseId = null;
    }
    this.iaCopyAccepted = false;
    this.showComparePanel = this.iaCopyResponses.length > 1
      || this.iaCopyResponses.some((r) => this.isIACopyResponsePending(r));
  }
}
